import React, { useLayoutEffect, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { sizes } from '../utils/breakpoints'
import { useMediaQuery } from 'react-responsive'
import Seo from '../components/Seo'
import LineaSVG from '../assets/images/LineaCurvaSiteMap.svg'
import LineaMobileSVG from '../assets/images/LineaCurvaMobileSiteMap.svg'

const CAREERS_FORM_ENDPOINT = process.env.REACT_APP_CAREERS_FORM_ENDPOINT

const TrabajaConNosotros = () => {
  const isMobile = useMediaQuery({ maxWidth: sizes.mobile })
  const linea = isMobile ? LineaMobileSVG : LineaSVG
  const [status, setStatus] = useState('idle') // idle | submitting | success | error
  const [fileName, setFileName] = useState('')
  const { t } = useTranslation()

  useLayoutEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const handleSubmit = async (event) => {
    event.preventDefault()

    if (!CAREERS_FORM_ENDPOINT) {
      setStatus('error')
      return
    }

    setStatus('submitting')
    const form = event.target

    try {
      const response = await fetch(CAREERS_FORM_ENDPOINT, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' },
      })

      if (response.ok) {
        setStatus('success')
        form.reset()
      } else {
        setStatus('error')
      }
    } catch (error) {
      setStatus('error')
    }
  }

  return (
    <PageWrapper>
      <Seo
        title={t('seo.talento.title')}
        description={t('seo.talento.description')}
        path="/talento"
      />

      <RelativeWrapper>
        <TitleRow>
          <Title>{t('talento.title')}</Title>
        </TitleRow>

        <ImgLinea src={linea} alt="" />

        <Container>
          <Card>
            <CardTitle>{t('talento.cardTitle')}</CardTitle>
            <Subtitle>{t('talento.subtitle')}</Subtitle>

            <FormWrapper onSubmit={handleSubmit}>
              <FormColumns>
                <FormLeft>
                  <FieldRow>
                    <Field>
                      <Label htmlFor="name">{t('talento.form.name')}</Label>
                      <Input id="name" name="fi-sender-fullName" type="text" required />
                    </Field>

                    <Field>
                      <Label htmlFor="email">{t('talento.form.email')}</Label>
                      <Input id="email" name="fi-sender-email" type="email" required />
                    </Field>
                  </FieldRow>

                  <Field>
                    <Label htmlFor="message">{t('talento.form.message')}</Label>
                    <Textarea id="message" name="fi-text-message" rows={4} />
                  </Field>
                </FormLeft>

                <FormRight>
                  <Field>
                    <Label htmlFor="cv">{t('talento.form.cv')}</Label>
                    <FileUpload>
                      <FileButton htmlFor="cv">{t('talento.form.cvButton')}</FileButton>
                      <FileName $hasFile={!!fileName}>
                        {fileName || t('talento.form.cvPlaceholder')}
                      </FileName>
                      <HiddenFileInput
                        id="cv"
                        name="fi-file-cv"
                        type="file"
                        accept=".pdf,.doc,.docx"
                        required
                        onChange={(event) =>
                          setFileName(event.target.files[0]?.name || '')
                        }
                      />
                    </FileUpload>
                  </Field>

                  <SubmitButton type="submit" disabled={status === 'submitting'}>
                    {status === 'submitting'
                      ? t('talento.form.submitting')
                      : t('talento.form.submit')}
                  </SubmitButton>

                  {status === 'success' && (
                    <StatusMessage tone="success">
                      {t('talento.form.success')}
                    </StatusMessage>
                  )}
                  {status === 'error' && (
                    <StatusMessage tone="error">
                      {t('talento.form.error')}{' '}
                      <a href="mailto:people@tartis.io">people@tartis.io</a>.
                    </StatusMessage>
                  )}
                </FormRight>
              </FormColumns>

              <ConsentField>
                <ConsentCheckbox id="consent" name="consent" type="checkbox" required />
                <ConsentLabel htmlFor="consent">
                  {t('talento.form.consent')}{' '}
                  <a href="/politica-privacidad">{t('talento.form.consentLink')}</a>.
                </ConsentLabel>
              </ConsentField>
            </FormWrapper>
          </Card>
        </Container>
      </RelativeWrapper>
    </PageWrapper>
  )
}

export default TrabajaConNosotros

const PageWrapper = styled.div`
  padding: 80px 24px 0;

  @media (min-width: ${sizes.mobile + 1}px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-height: calc(100vh - 80px);
  }

  @media (max-width: ${sizes.mobile}px) {
    padding: 70px 20px 0;
  }
`

const RelativeWrapper = styled.div`
  position: relative;
`

const Container = styled.div`
  max-width: 980px;
  margin: 0 auto;
  padding: 0 0 40px;

  @media (max-width: ${sizes.mobile}px) {
    padding: 0 0 60px;
  }
`

const TitleRow = styled.div`
  max-width: 980px;
  margin: 0 auto 24px;
  text-align: center;

  @media (max-width: ${sizes.mobile}px) {
    margin: 0 auto 12px;
  }
`

const Title = styled.h1`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 40px;
  line-height: 48px;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 24px;
    line-height: 28.8px;
  }
`

const ImgLinea = styled.img`
  position: absolute;
  top: 70px;
  left: 50%;
  transform: translateX(-50%);
  width: 100vw;
  max-width: 100vw;
  height: auto;
  z-index: 0;

  @media (max-width: ${sizes.mobile}px) {
    position: static;
    transform: none;
    left: auto;
    margin: 12px 0 32px calc(-50vw + 50%);
  }
`

const dropIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-28px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const Card = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  background-color: ${({ theme }) => theme.colors.background.card};
  border-radius: 24px;
  box-shadow: ${({ theme }) => theme.shadows.card};
  padding: 56px 72px;
  box-sizing: border-box;
  animation: ${dropIn} 0.6s cubic-bezier(0.16, 1, 0.3, 1) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (max-width: ${sizes.mobile}px) {
    padding: 32px 24px;
    border-radius: 16px;
  }
`

const CardTitle = styled.p`
  color: ${({ theme }) => theme.colors.text.strong};
  font-family: ${({ theme }) => theme.fonts.headingAlt};
  font-size: 26px;
  margin: 0 0 14px;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 19px;
    margin: 0 0 10px;
  }
`

const Subtitle = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 16px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 32px;

  @media (max-width: ${sizes.mobile}px) {
    font-size: 13px;
    line-height: 20px;
    margin: 0 0 24px;
  }
`

const FormWrapper = styled.form`
  display: flex;
  flex-direction: column;
`

const FormColumns = styled.div`
  display: flex;
  gap: 64px;

  @media (max-width: ${sizes.mobile}px) {
    flex-direction: column;
    gap: 24px;
  }
`

const FormLeft = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  gap: 14px;
`

const FormRight = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const Field = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const FieldRow = styled.div`
  display: flex;
  gap: 16px;

  @media (max-width: ${sizes.mobile}px) {
    flex-direction: column;
    gap: 14px;
  }
`

const Label = styled.label`
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.primary};
`

const inputStyles = css`
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.primary};
  padding: 12px 16px;
  border: 1px solid ${({ theme }) => theme.colors.border.input};
  border-radius: 8px;
  background-color: ${({ theme }) => theme.colors.background.card};
  transition: border-color 0.15s ease;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.border.focus};
  }
`

const Input = styled.input`
  ${inputStyles}
`

const Textarea = styled.textarea`
  ${inputStyles}
  resize: vertical;
`

const FileUpload = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const FileButton = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 15px;
  color: ${({ theme }) => theme.colors.text.primary};
  border: 1px solid ${({ theme }) => theme.colors.border.input};
  padding: 12px 20px;
  border-radius: 999px;
  cursor: pointer;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.text.accent};
  }
`

const FileName = styled.span`
  text-align: center;
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 13px;
  color: ${({ $hasFile, theme }) =>
    $hasFile ? theme.colors.text.primary : theme.colors.text.muted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const HiddenFileInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
`

const ConsentField = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-top: 28px;

  @media (max-width: ${sizes.mobile}px) {
    margin-top: 20px;
  }
`

const ConsentCheckbox = styled.input`
  width: 16px;
  height: 16px;
  margin: 2px 0 0;
  flex-shrink: 0;
  accent-color: ${({ theme }) => theme.colors.brand.green};
`

const ConsentLabel = styled.label`
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 14px;
  line-height: 20px;
  color: ${({ theme }) => theme.colors.text.primary};

  a {
    color: inherit;
  }
`

const SubmitButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.brand.dark};
  background-color: ${({ theme }) => theme.colors.brand.green};
  font-family: ${({ theme }) => theme.fonts.accentBold};
  font-size: 18px;
  border: none;
  padding: 14px 36px;
  border-radius: 999px;
  cursor: pointer;
  transition: background-color 0.15s ease, transform 0.15s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.brand.dark};
    color: ${({ theme }) => theme.colors.text.onDark};
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`

const StatusMessage = styled.p`
  font-family: ${({ theme }) => theme.fonts.accentRegular};
  font-size: 15px;
  color: ${({ tone, theme }) =>
    tone === 'success' ? theme.colors.feedback.success : theme.colors.feedback.error};

  a {
    color: inherit;
  }
`
